import { ColDef, RowClickedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { aggridHeaderClass } from 'src/components/common/common';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import Grid_Touch from 'src/components/common/gridWrapper/Grid_Touch';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { defaultColDef, getDefaultRowIndexColumnConfig } from 'src/utils/grid/gridStyleUtils';
import { formatDate } from 'src/utils/string/stringUtil';

import { defaultWMS1021_R01HeaderDto, WMS1021_R01HeaderDto } from './Dto_WMS1021_R01';
import { useWMS1021_R01Store } from './Store_WMS1021_R01';
import { onlyTouchGridStyle } from 'src/utils/grid/gridFunctionUtil';
import { cellDateComparator } from 'src/utils/grid/gridFunctionUtil';

const COLUMN_STATE_KEY = 'WMS1021_R01-grid-column-state';

interface Props {
	gridRef: React.RefObject<AgGridReact<WMS1021_R01HeaderDto> | null>;
	handleCreateClick: () => void;
	defaultWMS1021_R01HeaderRow: WMS1021_R01HeaderDto;
}

export const Table_WMS1021_R01 = (props: Props) => {
	const { gridRef, handleCreateClick, defaultWMS1021_R01HeaderRow } = props;

	const {
		WMS1021_R01List,
		selectedWMS1021_R01, setSelectedWMS1021_R01, initSelectedWMS1021_R01,
		columnState, setColumnState,
		scrollTop, setScrollTop,
		setDetailScrollTop,
	} = useWMS1021_R01Store();

	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

	const {
		getRowId,
		settingRowIndex,
		createRowClassRules,
		paddedGridData,
		ensureSelectedRow,
	} = useGridSettingContent({
		gridRef,
		key: 'wmsOrderNo',
		gridData: WMS1021_R01List,
		defaultGridData: defaultWMS1021_R01HeaderRow,
		selectedRow: selectedWMS1021_R01,
	});

	const headerNames: Record<string, string> = {
		rowIndex: '',
		compNm: '사업장',
		orderDt: '입고일자',
		customNm: '상호',
		orderAmt: '매출금액',
	};

	const getCellStyle = useCallback((params: unknown, key: string) => onlyTouchGridStyle(key, 'compNm')(params), []);

	const baseColumnDefs: ColDef<WMS1021_R01HeaderDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '사업장', colId: 'compNm', field: 'compNm', width: 110,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'compNm'),
		},
		{
			headerName: '입고일자', colId: 'orderDt', field: 'orderDt', width: 90,
			valueFormatter: (params: { data?: WMS1021_R01HeaderDto }) => {
				if (!params.data?.orderDt) return '';
				const formattedDate = formatDate(new Date(params.data.orderDt));
				return formattedDate === '1900-01-01' ? '입고필요' : formattedDate;
			},
			comparator: cellDateComparator,
			cellStyle: (p) => getCellStyle(p, 'orderDt'),
		},
		{
			headerName: '상호', colId: 'customNm', field: 'customNm', width: 180,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'customNm'),
		},
	], [getCellStyle, cellDateComparator]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs: ColDef<WMS1021_R01HeaderDto>[] = useMemo(() => {
		if (columnState === null) return baseColumnDefs;
		return GridCustomColumnDefs<WMS1021_R01HeaderDto>({
			baseColumnDefs,
			columnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [columnState, settingRowIndex, baseColumnDefs, headerNames]);

	const handleRowClick = useCallback((e: RowClickedEvent<WMS1021_R01HeaderDto>) => {
		if (e.node.rowPinned === 'bottom') return;
		const rowData = e.data;
		if (!rowData) return;

		const isSelected = selectedWMS1021_R01?.wmsOrderNo === rowData.wmsOrderNo;
		if (isSelected) {
			setSelectedWMS1021_R01(undefined);
			handleCreateClick();
		} else {
			setSelectedWMS1021_R01(rowData);
		}
		setDetailScrollTop(null);
	}, [selectedWMS1021_R01, setSelectedWMS1021_R01, handleCreateClick, setDetailScrollTop]);

	const pinnedBottomRow = useMemo(() => ([{
		rowIndex: '',
		compNm: `${WMS1021_R01List.length.toLocaleString()}건`,
		editable: false,
	}]), [WMS1021_R01List]);

	useEffect(() => { ensureSelectedRow(); }, [WMS1021_R01List, selectedWMS1021_R01]);

	return (
		<GridContextWrapper<WMS1021_R01HeaderDto>
			enableCellCalculation
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			initSelected={initSelectedWMS1021_R01}
			defaultRowData={defaultWMS1021_R01HeaderRow}
			baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Touch<WMS1021_R01HeaderDto>
				ref={gridRef}
				rowData={paddedGridData}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				getRowId={getRowId}
				rowClassRules={createRowClassRules<WMS1021_R01HeaderDto>({
					idKey: 'wmsOrderNo',
					selectedList: selectedWMS1021_R01 ? [selectedWMS1021_R01.wmsOrderNo] : [],
					deletedList: [],
				})}
				onRowClicked={handleRowClick}
				onCellKeyDown={undefined}
				pinnedBottomRowData={pinnedBottomRow}
				setHoveredRowIndex={setHoveredRowIndex}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setColumnState}
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
			/>
		</GridContextWrapper>
	);
};
